import { useState, useEffect } from "react";
import "./purchases.css";
import "./CRM.css"; // Reuse broad styles
import { useNavigate } from "react-router-dom";
import dataService from "../services/dataService";
import authService from "../services/authService";
import Modal from "../components/Modal";
import { useToast } from "../context/ToastContext";
import { formatCurrency } from "../utils/formatCurrency";
import { Eye, FileEdit, Trash2, Search, Plus, Download } from "lucide-react";

function PurchasesScreen() {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("view");
  const [formData, setFormData] = useState({});
  const [itemToDelete, setItemToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const { addToast } = useToast();

  const fetchPurchases = async () => {
    try {
      setLoading(true);
      const response = await dataService.getPurchases();
      setPurchases(response.data.data);
    } catch (error) {
      console.error("Failed to fetch purchases", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPurchases();
  }, []);

  const handleCreatePurchase = () => {
    navigate("/purchases/create");
  };

  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target.result;
      const rows = text.split('\n').map(row => row.split(','));
      
      const headers = rows[0].map(h => h.trim().replace(/"/g, ''));
      const idIdx = headers.findIndex(h => h.toLowerCase().includes('po') || h.toLowerCase().includes('id') || h.toLowerCase().includes('number'));
      const vendorIdx = headers.findIndex(h => h.toLowerCase().includes('vendor') || h.toLowerCase().includes('supplier'));
      const dateIdx = headers.findIndex(h => h.toLowerCase().includes('date'));
      const amountIdx = headers.findIndex(h => h.toLowerCase().includes('amount') || h.toLowerCase().includes('total'));
      const productIdx = headers.findIndex(h => h.toLowerCase().includes('product') || h.toLowerCase().includes('item'));
      const qtyIdx = headers.findIndex(h => h.toLowerCase().includes('quantity') || h.toLowerCase().includes('qty'));
      const costIdx = headers.findIndex(h => h.toLowerCase().includes('cost') || h.toLowerCase().includes('rate'));
      const statusIdx = headers.findIndex(h => h.toLowerCase().includes('status'));

      const newPurchases = [];
      for (let i = 1; i < rows.length; i++) {
        const row = rows[i];
        if (row.length < 2) continue;
        
        const amountVal = parseFloat((row[amountIdx] || '0').replace(/"/g, '').trim()) || 0;
        const qtyVal = parseInt((row[qtyIdx] || '1').replace(/"/g, '').trim(), 10) || 1;
        const costVal = parseFloat((row[costIdx] || '0').replace(/"/g, '').trim()) || 0;
        const prodName = (row[productIdx] || 'General Inventory').replace(/"/g, '').trim();

        newPurchases.push({
          purchaseId: (row[idIdx] || `PO-${Date.now().toString().slice(-4)}-${i}`).replace(/"/g, '').trim(),
          vendor: (row[vendorIdx] || 'Imported Supplier').replace(/"/g, '').trim(),
          date: (row[dateIdx] || new Date().toISOString().split('T')[0]).replace(/"/g, '').trim(),
          amount: amountVal || (qtyVal * costVal),
          items: [{
            product: prodName,
            quantity: qtyVal,
            cost: costVal || (amountVal / qtyVal),
            total: amountVal || (qtyVal * costVal)
          }],
          status: (row[statusIdx] || 'Pending').replace(/"/g, '').trim()
        });
      }

      if (newPurchases.length > 0) {
        addToast(`Importing ${newPurchases.length} purchase orders...`, "success");
        try {
          for (const po of newPurchases) {
            await dataService.addPurchase(po);
          }
          fetchPurchases();
          addToast("Import successful!", "success");
        } catch (err) {
          addToast("Error during import", "error");
        }
      }
    };
    reader.readAsText(file);
  };

  const handleView = (purchase) => {
    setSelectedPurchase(purchase);
    setModalMode("view");
    setIsModalOpen(true);
  };

  const handleEdit = (purchase) => {
    navigate(`/purchases/edit/${purchase._id}`);
  };

  const handleDeleteClick = (purchase) => {
    setItemToDelete(purchase);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await dataService.deletePurchase(itemToDelete._id);
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
      fetchPurchases();
      addToast("Purchase deleted successfully", "success");
    } catch (error) {
      addToast("Failed to delete purchase: " + error.message, "error");
    }
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    try {
      await dataService.updatePurchase(selectedPurchase._id, formData);
      setIsModalOpen(false);
      fetchPurchases();
      addToast("Purchase updated successfully", "success");
    } catch (error) {
      addToast("Failed to update purchase: " + error.message, "error");
    }
  };

  return (
    <div className="crm-screen">
      <div className="screen-header">
        <div className="header-info">
          <h1>Purchase Management</h1>
          <p>Track and manage purchase orders</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <label className="secondary-btn" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Download size={18} /> Import CSV
            <input type="file" accept=".csv" onChange={handleImportCSV} style={{ display: 'none' }} />
          </label>
          {authService.hasPermission('purchase', 'createPurchaseOrder') && (
            <button className="primary-btn" onClick={handleCreatePurchase}>
              <Plus size={18} /> Create Purchase Order
            </button>
          )}
        </div>
      </div>

      <div className="summary-grid">
        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-label">Total Orders</span>
            <h2 className="stat-value">{purchases.length}</h2>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-label">Total Amount</span>
            <h2 className="stat-value">{formatCurrency(purchases.reduce((acc, p) => acc + p.amount, 0))}</h2>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-label">Pending</span>
            <h2 className="stat-value" style={{ color: '#EA580C' }}>{purchases.filter(p => p.status === 'Pending').length}</h2>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-label">Completed</span>
            <h2 className="stat-value" style={{ color: '#00A859' }}>{purchases.filter(p => p.status === 'Completed').length}</h2>
          </div>
        </div>
      </div>

      <div className="content-card" style={{ marginTop: '32px' }}>
        <div className="card-controls">
          <h2 className="font-bold">Purchase Orders</h2>
          <div className="search-box">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Search orders by ID, vendor name, or status..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="table-wrapper">
          {loading ? (
            <p style={{ padding: '20px' }}>Loading purchases...</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>PO Number</th>
                  <th>Vendor</th>
                  <th>Date</th>
                  <th>Items</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {purchases
                  .filter(p =>
                    p.purchaseId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    (p.vendor && p.vendor.toLowerCase().includes(searchTerm.toLowerCase()))
                  )
                  .map((purchase) => (
                    <tr key={purchase._id}>
                      <td><span className="font-bold">{purchase.purchaseId}</span></td>
                      <td>{purchase.vendor || "N/A"}</td>
                      <td>{new Date(purchase.date).toLocaleDateString()}</td>
                      <td>{purchase.items?.length || 0}</td>
                      <td><span className="font-bold">{formatCurrency(purchase.amount)}</span></td>
                      <td>
                        <span className={`status-pill ${purchase.status.toLowerCase()}`}>
                          {purchase.status}
                        </span>
                      </td>
                      <td className="text-right">
                        <div className="action-icons">
                          <button className="icon-btn" title="View" onClick={() => handleView(purchase)}>
                            <Eye size={16} />
                          </button>

                          {authService.hasPermission('purchase', 'editPurchase') && (
                            <button className="icon-btn success" title="Edit" onClick={() => handleEdit(purchase)}>
                              <FileEdit size={16} />
                            </button>
                          )}

                          {authService.hasPermission('purchase', 'deletePurchase') && (
                            <button className="icon-btn danger" title="Delete" onClick={() => handleDeleteClick(purchase)}>
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                {purchases.length === 0 && (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>No purchase orders found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={modalMode === "view" ? "Purchase Details" : "Edit Purchase"}
        footer={
          modalMode === "edit" ? (
            <>
              <button className="secondary-btn" onClick={() => setIsModalOpen(false)}>Cancel</button>
              <button className="primary-btn" onClick={handleUpdateSubmit}>Save Changes</button>
            </>
          ) : (
            <button className="primary-btn" onClick={() => setIsModalOpen(false)}>Close</button>
          )
        }
      >
        {modalMode === "view" && selectedPurchase && (
          <div className="detail-view">
            <div className="detail-item">
              <span className="detail-label">PO Number</span>
              <span className="detail-value">{selectedPurchase.purchaseId}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Vendor</span>
              <span className="detail-value">{selectedPurchase.vendor}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Date</span>
              <span className="detail-value">{new Date(selectedPurchase.date).toLocaleDateString()}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Items</span>
              <div className="detail-value">
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {selectedPurchase.items?.map((item, idx) => (
                    <li key={idx} style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid #F3F4F6' }}>
                      {item.product} - {item.quantity} x {formatCurrency(item.cost)} = {formatCurrency(item.total)}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="detail-item">
              <span className="detail-label">Total Amount</span>
              <span className="detail-value" style={{ fontSize: '20px', fontWeight: 'bold' }}>{formatCurrency(selectedPurchase.amount)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Status</span>
              <span className={`status-pill ${selectedPurchase.status?.toLowerCase()}`}>
                {selectedPurchase.status}
              </span>
            </div>
          </div>
        )}

        {modalMode === "edit" && (
          <form className="modal-form" onSubmit={handleUpdateSubmit}>
            <div className="form-group">
              <label>PO Number</label>
              <input name="purchaseId" value={formData.purchaseId || ""} readOnly disabled />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select name="status" value={formData.status || ""} onChange={handleFormChange}>
                <option value="Pending">Pending</option>
                <option value="Received">Received</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
            <div className="form-group">
              <label>Total Amount Override</label>
              <input type="number" name="amount" value={formData.amount || 0} onChange={handleFormChange} />
            </div>
          </form>
        )}
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirm Deletion"
        footer={
          <>
            <button className="secondary-btn" onClick={() => setIsDeleteModalOpen(false)}>Cancel</button>
            <button className="primary-btn btn-danger" onClick={confirmDelete}>Delete Purchase</button>
          </>
        }
      >
        <div className="delete-confirm-view">
          <div className="delete-icon-wrapper">
            <Trash2 size={32} />
          </div>
          <h3>Are you sure?</h3>
          <p>You are about to delete purchase order <strong>{itemToDelete?.purchaseId}</strong>. This action cannot be undone.</p>
        </div>
      </Modal>
    </div>
  );
}

export default PurchasesScreen;
